/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Seed data for the buyer board:
 * nine supplier x ship-to carts and their replenishment suggestion lines.
 *
 * Quantities are not typed in: each line carries the net requirement inputs
 * (demand, on hand, on order, safety stock) and `buyer-board-logic.ts` derives
 * the engine's calculated quantity from them, so every number on the board is
 * consistent with its explanation. Dates are relative to today.
 *
 * Panel-shop (PNL) lines with a BOM trigger are the planned orders sent from
 * the BoM forecast page; they are seeded here, so no cross-store write is
 * needed. A tenth cart, Hollis to the panel shop, stays hidden until the
 * quality-hold note on the Vitrex cart adds its first line.
 */
import { getSite } from '@/demo/procurement/data/company';
import { fmtQtyWithUnit } from '@/demo/procurement/data/format';
import { getSupplier } from '@/demo/procurement/data/suppliers';
import type {
  Buyer,
  BuyerId,
  BoardCart,
  BoardItem,
  BoardLine,
  DemandRef,
  OpenDraftPo,
  ShipToId,
} from './buyer-board-types';

export const BUYERS: Record<BuyerId, Buyer> = {
  'ben-ramirez': {
    id: 'ben-ramirez',
    name: 'Ben Ramirez',
    shortName: 'B. Ramirez',
    initials: 'BR',
    code: 'B',
  },
  'dana-whitaker': {
    id: 'dana-whitaker',
    name: 'Dana Whitaker',
    shortName: 'D. Whitaker',
    initials: 'DW',
    code: 'D',
  },
  'luis-ortega': {
    id: 'luis-ortega',
    name: 'Luis Ortega',
    shortName: 'L. Ortega',
    initials: 'LO',
    code: 'L',
  },
  'carla-nguyen': {
    id: 'carla-nguyen',
    name: 'Carla Nguyen',
    shortName: 'C. Nguyen',
    initials: 'CN',
    code: 'C',
  },
};

export const BUYER_LIST: Buyer[] = Object.values(BUYERS);

export const SHIP_TO_IDS: ShipToId[] = ['DAL', 'PHX', 'HAY', 'PNL'];

/** The ERP warehouse code buyers know a ship-to by, where it differs from the name. */
export const SHIP_TO_ERP_CODE: Partial<Record<ShipToId, string>> = {
  HAY: 'CA-01',
};

/** Transfer transit time between DCs and the panel shop, days. */
export const TRANSFER_TRANSIT_DAYS: Record<
  ShipToId,
  Record<ShipToId, number>
> = {
  DAL: { DAL: 0, PHX: 3, HAY: 4, PNL: 1 },
  PHX: { DAL: 3, PHX: 0, HAY: 2, PNL: 3 },
  HAY: { DAL: 4, PHX: 2, HAY: 0, PNL: 4 },
  PNL: { DAL: 1, PHX: 3, HAY: 4, PNL: 0 },
};

function item(spec: BoardItem): BoardItem {
  return spec;
}

export const ITEMS: Record<string, BoardItem> = {
  // Wire and cable (Corvane, Hollis)
  'THHN-1/0-CU': item({
    code: 'THHN-1/0-CU',
    description: 'Copper building wire, 1/0 gauge, 500 ft reel',
    uom: 'C',
    packQty: 5,
    moq: 20,
    materialId: 'MAT-THHN',
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'THHN-12-BK': item({
    code: 'THHN-12-BK',
    description: 'Copper building wire, 12 gauge, black, 2,500 ft reel',
    uom: 'reel',
    packQty: 4,
    moq: 4,
    materialId: null,
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'THHN-10-BK': item({
    code: 'THHN-10-BK',
    description: 'Copper building wire, 10 gauge, black, 2,500 ft reel',
    uom: 'reel',
    packQty: 4,
    moq: 4,
    materialId: null,
    pop: 'B',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'THHN-14-WH': item({
    code: 'THHN-14-WH',
    description: 'Copper building wire, 14 gauge, white, 2,500 ft reel',
    uom: 'reel',
    packQty: 4,
    moq: 4,
    materialId: null,
    pop: 'C',
    consumptionMonths: 7,
    stockedSites: 1,
  }),
  'MC-12/2-250': item({
    code: 'MC-12/2-250',
    description: 'Metal-clad cable, 12/2 with ground, 250 ft coil',
    uom: 'coil',
    packQty: 10,
    moq: 20,
    materialId: null,
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'MC-14/2-250': item({
    code: 'MC-14/2-250',
    description: 'Metal-clad cable, 14/2 with ground, 250 ft coil',
    uom: 'coil',
    packQty: 10,
    moq: 20,
    materialId: null,
    pop: 'B',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'XHHW-4/0-AL': item({
    code: 'XHHW-4/0-AL',
    description: 'Aluminum building wire, 4/0 gauge, 500 ft reel',
    uom: 'C',
    packQty: 5,
    moq: 10,
    materialId: null,
    pop: 'B',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'XHHW-750-CU': item({
    code: 'XHHW-750-CU',
    description: 'Large copper wire, 750 kcmil, 500 ft reel (special order)',
    uom: 'C',
    packQty: 5,
    moq: 5,
    materialId: null,
    pop: 'N',
    consumptionMonths: 3,
    stockedSites: 1,
  }),

  // Circuit protection (Hollis, Vitrex)
  'MCB-20A-1P': item({
    code: 'MCB-20A-1P',
    description: 'Small circuit breaker, 20 A, 1 pole, plug-on',
    uom: 'each',
    packQty: 12,
    moq: 120,
    materialId: null,
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'MCCB-100A-3P': item({
    code: 'MCCB-100A-3P',
    description: 'Circuit breaker, 100 A, 3 pole, 480 V',
    uom: 'each',
    packQty: 1,
    moq: 4,
    materialId: null,
    pop: 'B',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'MCCB-250A-3P': item({
    code: 'MCCB-250A-3P',
    description: 'Circuit breaker, 250 A, 3 pole, 480 V',
    uom: 'each',
    packQty: 1,
    moq: 4,
    materialId: null,
    pop: 'B',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'MCCB-400A-3P': item({
    code: 'MCCB-400A-3P',
    description: 'Circuit breaker, 400 A, 3 pole, 480 V',
    uom: 'each',
    packQty: 1,
    moq: 2,
    materialId: null,
    pop: 'B',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'MCCB-800A': item({
    code: 'MCCB-800A',
    description: 'Circuit breaker, 800 A, 3 pole, 480 V, UL-listed',
    uom: 'each',
    packQty: 1,
    moq: 2,
    materialId: 'MAT-MCCB',
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'MCCB-800A-H': item({
    code: 'MCCB-800A-H',
    description: 'Circuit breaker, 800 A, 3 pole, 480 V, UL-listed',
    uom: 'each',
    packQty: 1,
    moq: 2,
    materialId: 'MAT-MCCB',
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 1,
  }),

  // Lighting (Aurelia)
  'LED-HB150-PP': item({
    code: 'LED-HB150-PP',
    description: 'Pillar Pro LED high-bay light, 150 W',
    uom: 'fixture',
    packQty: 4,
    moq: 40,
    materialId: 'MAT-LED',
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 3,
  }),
  'LED-HB200-PP': item({
    code: 'LED-HB200-PP',
    description: 'Pillar Pro LED high-bay light, 200 W',
    uom: 'fixture',
    packQty: 4,
    moq: 40,
    materialId: null,
    pop: 'B',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'LED-FL100-PP': item({
    code: 'LED-FL100-PP',
    description: 'Pillar Pro LED flood light, 100 W',
    uom: 'fixture',
    packQty: 6,
    moq: 24,
    materialId: null,
    pop: 'B',
    consumptionMonths: 12,
    stockedSites: 2,
  }),

  // Conduit (Meridian)
  'PVC-40-1IN': item({
    code: 'PVC-40-1IN',
    description: 'PVC conduit, 1 in, 10 ft lengths',
    uom: 'C',
    packQty: 10,
    moq: 100,
    materialId: 'MAT-PVC',
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 1,
  }),
  'PVC-40-2IN': item({
    code: 'PVC-40-2IN',
    description: 'PVC conduit, 2 in, 10 ft lengths',
    uom: 'C',
    packQty: 10,
    moq: 100,
    materialId: null,
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'PVC-ELB90-1IN': item({
    code: 'PVC-ELB90-1IN',
    description: 'PVC elbow, 1 in, 90 degree, case of 100',
    uom: 'case',
    packQty: 5,
    moq: 10,
    materialId: null,
    pop: 'B',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'PVC-CPL-1IN': item({
    code: 'PVC-CPL-1IN',
    description: 'PVC coupling, 1 in, case of 200',
    uom: 'case',
    packQty: 5,
    moq: 10,
    materialId: null,
    pop: 'C',
    consumptionMonths: 12,
    stockedSites: 1,
  }),

  // Enclosures (Polaris)
  'ENC-N4X-2420': item({
    code: 'ENC-N4X-2420',
    description: 'Corrosion-resistant fiberglass enclosure, 24 x 20 x 10 in',
    uom: 'each',
    packQty: 5,
    moq: 10,
    materialId: null,
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 1,
  }),
  'ENC-N12-7224': item({
    code: 'ENC-N12-7224',
    description: 'Floor-standing enclosure, 72 x 24 x 24 in',
    uom: 'each',
    packQty: 1,
    moq: 4,
    materialId: null,
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 1,
  }),
  'ENC-N12-CUST': item({
    code: 'ENC-N12-CUST',
    description: 'Industrial enclosure, 72 x 36 x 24 in, custom cutouts',
    uom: 'each',
    packQty: 1,
    moq: 1,
    materialId: null,
    pop: 'N',
    consumptionMonths: 2,
    stockedSites: 1,
  }),
  'ENC-WP-1612': item({
    code: 'ENC-WP-1612',
    description: 'Wall-mount enclosure, 16 x 12 x 8 in',
    uom: 'each',
    packQty: 10,
    moq: 20,
    materialId: null,
    pop: 'B',
    consumptionMonths: 12,
    stockedSites: 1,
  }),

  // Wiring devices (Clearwell)
  'WD-RCPT15-TR': item({
    code: 'WD-RCPT15-TR',
    description: 'Wall outlet, 15 A, tamper-resistant, white, case of 100',
    uom: 'case',
    packQty: 10,
    moq: 20,
    materialId: null,
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 3,
  }),
  'WD-SW15-DEC': item({
    code: 'WD-SW15-DEC',
    description: 'Light switch, 15 A, white, case of 100',
    uom: 'case',
    packQty: 10,
    moq: 20,
    materialId: null,
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 3,
  }),
  'WD-GFCI20-TR': item({
    code: 'WD-GFCI20-TR',
    description:
      'Ground-fault outlet, 20 A, tamper-resistant, white, carton of 10',
    uom: 'carton',
    packQty: 5,
    moq: 10,
    materialId: null,
    pop: 'B',
    consumptionMonths: 11,
    stockedSites: 2,
  }),
  'WD-WPC-1G': item({
    code: 'WD-WPC-1G',
    description: 'Weatherproof outlet cover, 1-gang, gray, case of 25',
    uom: 'case',
    packQty: 4,
    moq: 4,
    materialId: null,
    pop: 'C',
    consumptionMonths: 12,
    stockedSites: 1,
  }),

  // Drives (Lumen Drive)
  'VFD-50HP-480': item({
    code: 'VFD-50HP-480',
    description: 'Motor drive, 50 HP, 480 V',
    uom: 'each',
    packQty: 1,
    moq: 2,
    materialId: 'MAT-VFD',
    pop: 'A',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'VFD-25HP-480': item({
    code: 'VFD-25HP-480',
    description: 'Motor drive, 25 HP, 480 V',
    uom: 'each',
    packQty: 1,
    moq: 2,
    materialId: null,
    pop: 'B',
    consumptionMonths: 12,
    stockedSites: 2,
  }),
  'VFD-DBR-50': item({
    code: 'VFD-DBR-50',
    description: 'Braking resistor kit for 50 HP motor drives',
    uom: 'each',
    packQty: 1,
    moq: 2,
    materialId: null,
    pop: 'C',
    consumptionMonths: 9,
    stockedSites: 1,
  }),
};

export function getItem(code: string): BoardItem | undefined {
  return ITEMS[code];
}

const CONTRACT_CORV = 'SA-CORV-2024-017';
const CONTRACT_HOLL = 'SA-HOLL-2024-021';

const QUALITY_HOLD_NOTE = 'Supplier on quality hold — RSK-301';

interface LineSeed {
  n: number;
  cartId: string;
  type: BoardLine['type'];
  state?: BoardLine['state'];
  holdNote?: string;
  holdRef?: BoardLine['holdRef'];
  shipToId: ShipToId;
  itemCode: string;
  demandRef: DemandRef | null;
  trigger?: BoardLine['trigger'];
  needInDays: number;
  demand: number;
  onHand: number;
  onOrder: number;
  safetyStock: number;
  weeklyUsage: number;
  qtyOverride?: number;
  plan?: BoardLine['plan'];
  transferQty?: number;
  transferFrom?: ShipToId;
  priority: BoardLine['priority'];
  unitCost: number;
  listPrice?: number;
  costSource: BoardLine['costSource'];
  why?: string[];
}

function defaultTrigger(type: BoardLine['type']): BoardLine['trigger'] {
  return type === 'stock' ? 'min_max' : 'so';
}

function line(seed: LineSeed): BoardLine {
  return {
    id: `${seed.cartId}-L${seed.n}`,
    cartId: seed.cartId,
    type: seed.type,
    state: seed.state ?? 'unapproved',
    holdNote: seed.holdNote ?? null,
    holdRef: seed.holdRef ?? null,
    shipToId: seed.shipToId,
    itemCode: seed.itemCode,
    demandRef: seed.demandRef,
    needInDays: seed.needInDays,
    demand: seed.demand,
    onHand: seed.onHand,
    onOrder: seed.onOrder,
    safetyStock: seed.safetyStock,
    weeklyUsage: seed.weeklyUsage,
    qtyOverride: seed.qtyOverride ?? null,
    plan: seed.plan ?? 'buy',
    transferQty: seed.transferQty ?? 0,
    transferFrom: seed.transferFrom ?? null,
    trigger: seed.trigger ?? defaultTrigger(seed.type),
    priority: seed.priority,
    unitCost: seed.unitCost,
    listPrice: seed.listPrice ?? null,
    costSource: seed.costSource,
    why: seed.why ?? [],
    forwardBuy: null,
    origin: 'engine',
  };
}

function so(orderNo: string, customer: string): DemandRef {
  return { kind: 'so', orderNo, customer };
}

function bom(finishedGood: string, builds: number): DemandRef {
  return { kind: 'bom', finishedGood, builds };
}

const CORV_PRICE: BoardLine['costSource'] = {
  kind: 'contract',
  contractId: CONTRACT_CORV,
  clauseSection: '3.3',
};

const HOLL_PRICE: BoardLine['costSource'] = {
  kind: 'contract',
  contractId: CONTRACT_HOLL,
  clauseSection: '2.1',
};

/**
 * What the Vitrex quality-hold note adds: Hollis's qualified 800 A breaker for
 * the panels the held lot puts at risk. It lands in the Hollis to panel shop
 * cart, which stays hidden until this line exists.
 */
const ALTERNATE_LINE: BoardLine = {
  ...line({
    n: 1,
    cartId: 'CRT-HOLL-PNL',
    type: 'backorder',
    state: 'approved',
    shipToId: 'PNL',
    itemCode: 'MCCB-800A-H',
    demandRef: bom('PPC-MCC-480', 22),
    trigger: 'bom',
    needInDays: 45,
    demand: 34,
    onHand: 0,
    onOrder: 0,
    safetyStock: 6,
    weeklyUsage: 14,
    priority: 'critical',
    unitCost: 4_020,
    listPrice: 4_150,
    costSource: HOLL_PRICE,
    why: [
      `The Vitrex hold puts 34 panels at risk over the next 8 weeks. ${getSupplier('sup-hollis').shortName} is the qualified alternate for this breaker, with a 5-week delivery time that lands before the ${getSite('PNL').name} runs out of Vitrex stock.`,
      '40 breakers cover the 34 panels plus safety stock, at $170 more per breaker than Vitrex ($6,800). Switch back once corrective action request SCAR-0192 closes.',
    ],
  }),
  origin: 'added',
};

export const CARTS: BoardCart[] = [
  {
    id: 'CRT-CORV-DAL',
    supplierId: 'sup-corvane',
    shipToId: 'DAL',
    buyerId: 'ben-ramirez',
    contractId: CONTRACT_CORV,
    mov: 50_000,
    freight: { kind: 'included', incoterm: 'DAP', clauseSection: '5.1' },
    leadTime: {
      observedWeeks: 8.6,
      receipts: 6,
      committedWeeks: 8,
      clauseSection: '6.2',
    },
    flag: null,
    hold: null,
    priceBreak: {
      itemCode: 'THHN-1/0-CU',
      addQty: 2_000,
      tier: 3,
      tierPrice: 418.0,
      ladder: [
        { tier: 1, label: 'Under 600,000 ft a quarter', price: 424.36 },
        { tier: 2, label: '600,000 to 999,900 ft', price: 421.2 },
        { tier: 3, label: '1,000,000 ft and over', price: 418.0 },
      ],
    },
    alternate: null,
  },
  {
    id: 'CRT-CORV-PHX',
    supplierId: 'sup-corvane',
    shipToId: 'PHX',
    buyerId: 'dana-whitaker',
    contractId: CONTRACT_CORV,
    mov: 50_000,
    freight: { kind: 'included', incoterm: 'DAP', clauseSection: '5.1' },
    leadTime: {
      observedWeeks: 8.4,
      receipts: 6,
      committedWeeks: 8,
      clauseSection: '6.2',
    },
    flag: null,
    hold: null,
    priceBreak: null,
    alternate: null,
  },
  {
    id: 'CRT-HOLL-DAL',
    supplierId: 'sup-hollis',
    shipToId: 'DAL',
    buyerId: 'ben-ramirez',
    contractId: CONTRACT_HOLL,
    mov: 25_000,
    freight: { kind: 'included', incoterm: 'DAP', clauseSection: '4.1' },
    leadTime: {
      observedWeeks: 5.2,
      receipts: 6,
      committedWeeks: 5,
      clauseSection: '4.2',
    },
    flag: null,
    hold: null,
    priceBreak: null,
    alternate: null,
  },
  {
    id: 'CRT-AURE-PHX',
    supplierId: 'sup-aurelia',
    shipToId: 'PHX',
    buyerId: 'dana-whitaker',
    contractId: 'SA-AURE-2024-009',
    mov: 25_000,
    freight: { kind: 'included', incoterm: 'DAP', clauseSection: '5.1' },
    leadTime: {
      observedWeeks: 6.4,
      receipts: 6,
      committedWeeks: 6,
      clauseSection: '6.1',
    },
    flag: null,
    hold: null,
    priceBreak: null,
    alternate: null,
  },
  {
    id: 'CRT-MERI-DAL',
    supplierId: 'sup-meridian',
    shipToId: 'DAL',
    buyerId: 'ben-ramirez',
    contractId: 'SA-MERI-2025-001',
    mov: 10_000,
    freight: { kind: 'included', incoterm: 'DAP', clauseSection: '4.1' },
    leadTime: {
      observedWeeks: 3.1,
      receipts: 8,
      committedWeeks: 3,
      clauseSection: null,
    },
    flag: null,
    hold: null,
    priceBreak: null,
    alternate: null,
  },
  {
    id: 'CRT-POLR-PNL',
    supplierId: 'sup-polaris',
    shipToId: 'PNL',
    buyerId: 'luis-ortega',
    contractId: 'SA-POLR-2022-008',
    mov: 20_000,
    freight: { kind: 'threshold', amount: 35_000 },
    leadTime: {
      observedWeeks: 6.4,
      receipts: 5,
      committedWeeks: 6,
      clauseSection: null,
    },
    flag: null,
    hold: null,
    priceBreak: null,
    alternate: null,
  },
  {
    id: 'CRT-CLRW-HAY',
    supplierId: 'sup-clearwell',
    shipToId: 'HAY',
    buyerId: 'luis-ortega',
    contractId: 'SA-CLRW-2024-006',
    mov: 15_000,
    freight: { kind: 'included', incoterm: 'DAP', clauseSection: '4.1' },
    leadTime: {
      observedWeeks: 4.2,
      receipts: 6,
      committedWeeks: 4,
      clauseSection: null,
    },
    flag: null,
    hold: null,
    priceBreak: null,
    alternate: null,
  },
  {
    id: 'CRT-LUMN-PNL',
    supplierId: 'sup-lumen',
    shipToId: 'PNL',
    buyerId: 'luis-ortega',
    contractId: 'SA-LUMN-2025-002',
    mov: 50_000,
    freight: { kind: 'included', incoterm: 'DDP', clauseSection: '5.1' },
    leadTime: {
      observedWeeks: 12.6,
      receipts: 4,
      committedWeeks: 12,
      clauseSection: null,
    },
    flag: {
      kind: 'tariff_dispute',
      label: 'Tariff dispute',
      detail:
        'Invoice LB-26-3391 carries a 25% tariff surcharge ($82,500) that SA-LUMN-2025-002 section 5.3 puts on the supplier.',
      note: 'Invoice LB-26-3391 has an $82,500 tariff charge the agreement puts on Lumen',
      invoiceId: 'LB-26-3391',
    },
    hold: null,
    priceBreak: null,
    alternate: null,
  },
  {
    id: 'CRT-VITX-PNL',
    supplierId: 'sup-vitrex',
    shipToId: 'PNL',
    buyerId: 'carla-nguyen',
    contractId: 'SA-VITX-2023-004',
    mov: 30_000,
    freight: { kind: 'included', incoterm: 'DAP', clauseSection: null },
    leadTime: {
      observedWeeks: 10.4,
      receipts: 4,
      committedWeeks: 10,
      clauseSection: null,
    },
    flag: {
      kind: 'quality_hold',
      label: 'Quality hold',
      detail:
        'Lot VX-24117 of 800 A circuit breakers came from a factory that UL has not certified (QA-VITX-2023-004 section 7.2). Corrective action request SCAR-0192 is open.',
      note: 'Breaker lot VX-24117 came from a factory UL has not certified',
      alertId: 'RSK-301',
    },
    hold: { reason: QUALITY_HOLD_NOTE, alertId: 'RSK-301' },
    priceBreak: null,
    alternate: { weeks: 6, premiumPerUnit: 170, line: ALTERNATE_LINE },
  },
  // Empty until the Vitrex alternate is added, so the board opens on nine carts.
  {
    id: 'CRT-HOLL-PNL',
    supplierId: 'sup-hollis',
    shipToId: 'PNL',
    buyerId: 'carla-nguyen',
    contractId: CONTRACT_HOLL,
    mov: 25_000,
    freight: { kind: 'included', incoterm: 'DAP', clauseSection: '4.1' },
    leadTime: {
      observedWeeks: 5.2,
      receipts: 6,
      committedWeeks: 5,
      clauseSection: '4.2',
    },
    flag: null,
    hold: null,
    priceBreak: null,
    alternate: null,
  },
];

export function getCart(id: string): BoardCart | undefined {
  return CARTS.find((cart) => cart.id === id);
}

export const DEFAULT_CART_ID = 'CRT-CORV-DAL';

const PHX_NAME = getSite('PHX').name;
const PNL_NAME = getSite('PNL').name;

export const SEED_LINES: BoardLine[] = [
  // Corvane -> Dallas DC
  line({
    n: 1,
    cartId: 'CRT-CORV-DAL',
    type: 'backorder',
    state: 'approved',
    shipToId: 'DAL',
    itemCode: 'THHN-1/0-CU',
    demandRef: so('48310', 'Acme Industrial'),
    needInDays: 58,
    demand: 1_700,
    onHand: 575,
    onOrder: 1_000,
    safetyStock: 425,
    weeklyUsage: 850,
    plan: 'buy_transfer',
    transferQty: 100,
    transferFrom: 'PHX',
    priority: 'critical',
    unitCost: 424.36,
    listPrice: 445,
    costSource: CORV_PRICE,
    why: [
      `Open back orders for 1/0 copper wire total ${fmtQtyWithUnit(1_700, 'C')}; the largest is Acme Industrial's feeder package, which ships the week of the need date.`,
      `Deliveries have been taking 8.6 weeks, which lands two days after the need date, so ${fmtQtyWithUnit(100, 'C')} is covered by moving stock from ${PHX_NAME}.`,
    ],
  }),
  line({
    n: 2,
    cartId: 'CRT-CORV-DAL',
    type: 'backorder',
    state: 'approved',
    shipToId: 'DAL',
    itemCode: 'THHN-12-BK',
    demandRef: so('48327', 'Brazos Electric'),
    needInDays: 63,
    demand: 18,
    onHand: 4,
    onOrder: 0,
    safetyStock: 2,
    weeklyUsage: 3,
    qtyOverride: 12,
    priority: 'urgent',
    unitCost: 712.4,
    costSource: { kind: 'list' },
    why: [
      'You trimmed this line to 12 reels: customer order 48327 only draws 12 reels (30,000 ft) before the next planned order lands.',
    ],
  }),
  line({
    n: 3,
    cartId: 'CRT-CORV-DAL',
    type: 'stock',
    trigger: 'deficit',
    shipToId: 'DAL',
    itemCode: 'THHN-10-BK',
    demandRef: null,
    needInDays: 70,
    demand: 40,
    onHand: 12,
    onOrder: 0,
    safetyStock: 10,
    weeklyUsage: 10,
    priority: 'standard',
    unitCost: 538.2,
    costSource: { kind: 'tier', tier: 2 },
  }),
  line({
    n: 4,
    cartId: 'CRT-CORV-DAL',
    type: 'stock',
    trigger: 'deficit',
    state: 'approved',
    shipToId: 'DAL',
    itemCode: 'MC-12/2-250',
    demandRef: null,
    needInDays: 72,
    demand: 60,
    onHand: 22,
    onOrder: 0,
    safetyStock: 14,
    weeklyUsage: 14,
    priority: 'standard',
    unitCost: 164.3,
    costSource: { kind: 'list' },
  }),
  line({
    n: 5,
    cartId: 'CRT-CORV-DAL',
    type: 'potf',
    shipToId: 'DAL',
    itemCode: 'XHHW-750-CU',
    demandRef: so('48335', 'Acme Industrial'),
    needInDays: 74,
    demand: 10,
    onHand: 0,
    onOrder: 0,
    safetyStock: 0,
    weeklyUsage: 1,
    priority: 'standard',
    unitCost: 1_296,
    costSource: { kind: 'list' },
    why: [
      'Not a stocked item: it is bought just for this one customer order and is not restocked afterwards.',
    ],
  }),
  line({
    n: 6,
    cartId: 'CRT-CORV-DAL',
    type: 'stock',
    shipToId: 'DAL',
    itemCode: 'MC-14/2-250',
    demandRef: null,
    needInDays: 77,
    demand: 80,
    onHand: 44,
    onOrder: 0,
    safetyStock: 20,
    weeklyUsage: 10,
    priority: 'fill',
    unitCost: 118.4,
    costSource: { kind: 'list' },
  }),
  line({
    n: 7,
    cartId: 'CRT-CORV-DAL',
    type: 'stock',
    state: 'rejected',
    holdNote: 'Rejected by buyer: covered by open PO-4500187',
    shipToId: 'DAL',
    itemCode: 'THHN-14-WH',
    demandRef: null,
    needInDays: 80,
    demand: 12,
    onHand: 4,
    onOrder: 0,
    safetyStock: 3,
    weeklyUsage: 1,
    priority: 'fill',
    unitCost: 596,
    costSource: { kind: 'list' },
  }),

  // Corvane -> Phoenix DC
  line({
    n: 1,
    cartId: 'CRT-CORV-PHX',
    type: 'backorder',
    state: 'approved',
    shipToId: 'PHX',
    itemCode: 'THHN-1/0-CU',
    demandRef: so('48355', 'Sunbelt Mechanical'),
    needInDays: 56,
    demand: 640,
    onHand: 480,
    onOrder: 100,
    safetyStock: 0,
    weeklyUsage: 560,
    priority: 'urgent',
    unitCost: 424.36,
    listPrice: 445,
    costSource: CORV_PRICE,
    why: [
      `Open back orders at ${PHX_NAME} are mostly covered by the ${fmtQtyWithUnit(100, 'C')} already on order, so only the remaining gap is bought.`,
    ],
  }),
  line({
    n: 2,
    cartId: 'CRT-CORV-PHX',
    type: 'stock',
    trigger: 'deficit',
    state: 'approved',
    shipToId: 'PHX',
    itemCode: 'XHHW-4/0-AL',
    demandRef: null,
    needInDays: 61,
    demand: 30,
    onHand: 18,
    onOrder: 0,
    safetyStock: 8,
    weeklyUsage: 6,
    plan: 'transfer',
    transferQty: 20,
    transferFrom: 'DAL',
    priority: 'standard',
    unitCost: 612,
    costSource: { kind: 'list' },
  }),
  line({
    n: 3,
    cartId: 'CRT-CORV-PHX',
    type: 'stock',
    shipToId: 'PHX',
    itemCode: 'THHN-10-BK',
    demandRef: null,
    needInDays: 66,
    demand: 36,
    onHand: 14,
    onOrder: 0,
    safetyStock: 9,
    weeklyUsage: 9,
    priority: 'fill',
    unitCost: 538.2,
    costSource: { kind: 'tier', tier: 2 },
  }),

  // Hollis -> Dallas DC
  line({
    n: 1,
    cartId: 'CRT-HOLL-DAL',
    type: 'backorder',
    state: 'approved',
    shipToId: 'DAL',
    itemCode: 'THHN-1/0-CU',
    demandRef: so('48366', 'Acme Industrial'),
    needInDays: 38,
    demand: 1_000,
    onHand: 250,
    onOrder: 450,
    safetyStock: 0,
    weeklyUsage: 540,
    priority: 'critical',
    unitCost: 437,
    listPrice: 452,
    costSource: HOLL_PRICE,
    why: [
      `${getSupplier('sup-hollis').shortName} is qualified for 33% of 1/0 copper wire volume with a 5-week delivery time, the fastest source for this need date.`,
    ],
  }),
  line({
    n: 2,
    cartId: 'CRT-HOLL-DAL',
    type: 'stock',
    trigger: 'deficit',
    shipToId: 'DAL',
    itemCode: 'MCB-20A-1P',
    demandRef: null,
    needInDays: 44,
    demand: 3_600,
    onHand: 1_100,
    onOrder: 0,
    safetyStock: 900,
    weeklyUsage: 900,
    priority: 'standard',
    unitCost: 9.85,
    costSource: { kind: 'list' },
  }),
  line({
    n: 3,
    cartId: 'CRT-HOLL-DAL',
    type: 'stock',
    state: 'trouble',
    holdNote:
      'Buyer hold: carton labels do not match UL certification records for lot HP-26-0417 (Priya Raman)',
    shipToId: 'DAL',
    itemCode: 'MCCB-100A-3P',
    demandRef: null,
    needInDays: 49,
    demand: 36,
    onHand: 14,
    onOrder: 0,
    safetyStock: 8,
    weeklyUsage: 4,
    priority: 'fill',
    unitCost: 386,
    costSource: { kind: 'list' },
  }),

  // Aurelia -> Phoenix DC
  line({
    n: 1,
    cartId: 'CRT-AURE-PHX',
    type: 'backorder',
    state: 'approved',
    shipToId: 'PHX',
    itemCode: 'LED-HB150-PP',
    demandRef: so('48375', 'Sunbelt Mechanical'),
    needInDays: 48,
    demand: 1_900,
    onHand: 700,
    onOrder: 600,
    safetyStock: 400,
    weeklyUsage: 520,
    priority: 'urgent',
    unitCost: 88.7,
    listPrice: 94.5,
    costSource: {
      kind: 'contract',
      contractId: 'SA-AURE-2024-009',
      clauseSection: '3.1',
    },
  }),
  line({
    n: 2,
    cartId: 'CRT-AURE-PHX',
    type: 'stock',
    trigger: 'deficit',
    shipToId: 'PHX',
    itemCode: 'LED-HB200-PP',
    demandRef: null,
    needInDays: 51,
    demand: 900,
    onHand: 360,
    onOrder: 300,
    safetyStock: 200,
    weeklyUsage: 140,
    priority: 'standard',
    unitCost: 112.4,
    costSource: { kind: 'list' },
  }),
  line({
    n: 3,
    cartId: 'CRT-AURE-PHX',
    type: 'stock',
    shipToId: 'PHX',
    itemCode: 'LED-FL100-PP',
    demandRef: null,
    needInDays: 55,
    demand: 240,
    onHand: 96,
    onOrder: 0,
    safetyStock: 60,
    weeklyUsage: 24,
    priority: 'fill',
    unitCost: 61.4,
    costSource: { kind: 'list' },
  }),

  // Meridian -> Dallas DC
  line({
    n: 1,
    cartId: 'CRT-MERI-DAL',
    type: 'backorder',
    state: 'approved',
    shipToId: 'DAL',
    itemCode: 'PVC-40-1IN',
    demandRef: so('48388', 'Brazos Electric'),
    needInDays: 22,
    demand: 2_600,
    onHand: 640,
    onOrder: 400,
    safetyStock: 300,
    weeklyUsage: 640,
    priority: 'urgent',
    unitCost: 58.4,
    listPrice: 61.2,
    costSource: {
      kind: 'contract',
      contractId: 'SA-MERI-2025-001',
      clauseSection: '2.1',
    },
  }),
  line({
    n: 2,
    cartId: 'CRT-MERI-DAL',
    type: 'stock',
    trigger: 'deficit',
    shipToId: 'DAL',
    itemCode: 'PVC-40-2IN',
    demandRef: null,
    needInDays: 29,
    demand: 500,
    onHand: 240,
    onOrder: 0,
    safetyStock: 130,
    weeklyUsage: 120,
    priority: 'standard',
    unitCost: 104.8,
    costSource: { kind: 'list' },
  }),
  line({
    n: 3,
    cartId: 'CRT-MERI-DAL',
    type: 'stock',
    shipToId: 'DAL',
    itemCode: 'PVC-ELB90-1IN',
    demandRef: null,
    needInDays: 33,
    demand: 40,
    onHand: 14,
    onOrder: 0,
    safetyStock: 10,
    weeklyUsage: 4,
    priority: 'fill',
    unitCost: 46.5,
    costSource: { kind: 'list' },
  }),
  line({
    n: 4,
    cartId: 'CRT-MERI-DAL',
    type: 'stock',
    shipToId: 'DAL',
    itemCode: 'PVC-CPL-1IN',
    demandRef: null,
    needInDays: 36,
    demand: 25,
    onHand: 10,
    onOrder: 0,
    safetyStock: 6,
    weeklyUsage: 2,
    priority: 'fill',
    unitCost: 38.2,
    costSource: { kind: 'list' },
  }),

  // Polaris -> Dallas panel shop (planned orders from the BoM forecast)
  line({
    n: 1,
    cartId: 'CRT-POLR-PNL',
    type: 'backorder',
    trigger: 'bom',
    state: 'approved',
    shipToId: 'PNL',
    itemCode: 'ENC-N4X-2420',
    demandRef: bom('KIT-VFD-50', 60),
    needInDays: -2,
    demand: 185,
    onHand: 40,
    onOrder: 50,
    safetyStock: 5,
    weeklyUsage: 14,
    priority: 'critical',
    unitCost: 276.4,
    costSource: { kind: 'tier', tier: 2 },
    why: [
      `KIT-VFD-50 retrofit kits are waiting on corrosion-resistant enclosures: the ${PNL_NAME} ran out two days ago and the order is already overdue.`,
      'Polaris is the only source for these enclosures (supplier risk RSK-300), so there is no alternate supplier to rush the order from.',
    ],
  }),
  line({
    n: 2,
    cartId: 'CRT-POLR-PNL',
    type: 'stock',
    trigger: 'bom',
    shipToId: 'PNL',
    itemCode: 'ENC-N12-7224',
    demandRef: bom('PPC-MCC-480', 22),
    needInDays: 52,
    demand: 60,
    onHand: 12,
    onOrder: 0,
    safetyStock: 8,
    weeklyUsage: 14,
    priority: 'urgent',
    unitCost: 1_184,
    costSource: { kind: 'list' },
    why: [
      'PPC-MCC-480 x 22 and PPC-PDP-208 x 38 each take one floor-standing enclosure. Deliveries have been taking 6.4 weeks, which lands before the need date.',
    ],
  }),
  line({
    n: 3,
    cartId: 'CRT-POLR-PNL',
    type: 'potf',
    shipToId: 'PNL',
    itemCode: 'ENC-N12-CUST',
    demandRef: so('48377', 'Acme Industrial'),
    needInDays: 40,
    demand: 2,
    onHand: 0,
    onOrder: 0,
    safetyStock: 0,
    weeklyUsage: 1,
    priority: 'standard',
    unitCost: 2_180,
    costSource: { kind: 'list' },
    why: [
      "Custom cutouts to Acme Industrial's drawing, so the enclosure is bought just for this one panel order.",
    ],
  }),
  line({
    n: 4,
    cartId: 'CRT-POLR-PNL',
    type: 'stock',
    shipToId: 'PNL',
    itemCode: 'ENC-WP-1612',
    demandRef: null,
    needInDays: 44,
    demand: 60,
    onHand: 26,
    onOrder: 0,
    safetyStock: 16,
    weeklyUsage: 8,
    priority: 'fill',
    unitCost: 74.6,
    costSource: { kind: 'list' },
  }),

  // Clearwell -> Hayward DC
  line({
    n: 1,
    cartId: 'CRT-CLRW-HAY',
    type: 'backorder',
    state: 'approved',
    shipToId: 'HAY',
    itemCode: 'WD-RCPT15-TR',
    demandRef: so('48402', 'Bayside Electric'),
    needInDays: 30,
    demand: 480,
    onHand: 120,
    onOrder: 160,
    safetyStock: 80,
    weeklyUsage: 110,
    priority: 'urgent',
    unitCost: 96.4,
    listPrice: 101,
    costSource: {
      kind: 'contract',
      contractId: 'SA-CLRW-2024-006',
      clauseSection: '2.1',
    },
  }),
  line({
    n: 2,
    cartId: 'CRT-CLRW-HAY',
    type: 'stock',
    trigger: 'deficit',
    shipToId: 'HAY',
    itemCode: 'WD-SW15-DEC',
    demandRef: null,
    needInDays: 33,
    demand: 160,
    onHand: 60,
    onOrder: 0,
    safetyStock: 40,
    weeklyUsage: 18,
    priority: 'standard',
    unitCost: 118.5,
    listPrice: 124,
    costSource: {
      kind: 'contract',
      contractId: 'SA-CLRW-2024-006',
      clauseSection: '2.1',
    },
  }),
  line({
    n: 3,
    cartId: 'CRT-CLRW-HAY',
    type: 'stock',
    shipToId: 'HAY',
    itemCode: 'WD-GFCI20-TR',
    demandRef: null,
    needInDays: 38,
    demand: 60,
    onHand: 20,
    onOrder: 0,
    safetyStock: 15,
    weeklyUsage: 8,
    priority: 'fill',
    unitCost: 142,
    listPrice: 148,
    costSource: {
      kind: 'contract',
      contractId: 'SA-CLRW-2024-006',
      clauseSection: '2.1',
    },
  }),
  line({
    n: 4,
    cartId: 'CRT-CLRW-HAY',
    type: 'stock',
    shipToId: 'HAY',
    itemCode: 'WD-WPC-1G',
    demandRef: null,
    needInDays: 41,
    demand: 16,
    onHand: 5,
    onOrder: 0,
    safetyStock: 4,
    weeklyUsage: 2,
    priority: 'fill',
    unitCost: 88.4,
    costSource: { kind: 'list' },
  }),

  // Lumen Drive -> Dallas panel shop
  line({
    n: 1,
    cartId: 'CRT-LUMN-PNL',
    type: 'backorder',
    trigger: 'bom',
    state: 'credit_hold',
    holdNote: 'Payment hold — disputed invoice LB-26-3391',
    holdRef: { kind: 'invoice', id: 'LB-26-3391' },
    shipToId: 'PNL',
    itemCode: 'VFD-50HP-480',
    demandRef: bom('PPC-MCC-480', 24),
    needInDays: 86,
    demand: 209,
    onHand: 40,
    onOrder: 120,
    safetyStock: 20,
    weeklyUsage: 40,
    priority: 'urgent',
    unitCost: 5_500,
    costSource: {
      kind: 'contract',
      contractId: 'SA-LUMN-2025-002',
      clauseSection: '4.1',
    },
    why: [
      'PPC-MCC-480 takes six motor drives per panel and KIT-VFD-50 takes one. Lumen Drive is the only qualified source of 50 HP drives, and deliveries have been taking 12.6 weeks, which leaves no room to wait.',
    ],
  }),
  line({
    n: 2,
    cartId: 'CRT-LUMN-PNL',
    type: 'stock',
    trigger: 'deficit',
    shipToId: 'PNL',
    itemCode: 'VFD-25HP-480',
    demandRef: null,
    needInDays: 88,
    demand: 40,
    onHand: 10,
    onOrder: 0,
    safetyStock: 8,
    weeklyUsage: 8,
    priority: 'standard',
    unitCost: 3_150,
    costSource: { kind: 'list' },
  }),
  line({
    n: 3,
    cartId: 'CRT-LUMN-PNL',
    type: 'stock',
    shipToId: 'PNL',
    itemCode: 'VFD-DBR-50',
    demandRef: null,
    needInDays: 84,
    demand: 12,
    onHand: 4,
    onOrder: 0,
    safetyStock: 3,
    weeklyUsage: 1,
    priority: 'fill',
    unitCost: 480,
    costSource: { kind: 'list' },
  }),

  // Vitrex -> Dallas panel shop (planned orders from the BoM forecast)
  line({
    n: 1,
    cartId: 'CRT-VITX-PNL',
    type: 'backorder',
    trigger: 'bom',
    state: 'trouble',
    holdNote: QUALITY_HOLD_NOTE,
    holdRef: { kind: 'alert', id: 'RSK-301' },
    shipToId: 'PNL',
    itemCode: 'MCCB-800A',
    demandRef: bom('PPC-MCC-480', 22),
    needInDays: 45,
    demand: 150,
    onHand: 84,
    onOrder: 0,
    safetyStock: 14,
    weeklyUsage: 14,
    priority: 'urgent',
    unitCost: 3_850,
    costSource: {
      kind: 'contract',
      contractId: 'SA-VITX-2023-004',
      clauseSection: '2.1',
    },
    why: [
      `The ${PNL_NAME} holds 6 weeks of 800 A circuit breakers. Lot VX-24117 is set aside, so the next Vitrex shipment cannot be accepted until corrective action request SCAR-0192 closes.`,
      'PPC-MCC-480 and PPC-PDP-208 each take one 800 A breaker: 34 panels are at risk over the next 8 weeks.',
    ],
  }),
  line({
    n: 2,
    cartId: 'CRT-VITX-PNL',
    type: 'stock',
    trigger: 'deficit',
    state: 'trouble',
    holdNote: QUALITY_HOLD_NOTE,
    holdRef: { kind: 'alert', id: 'RSK-301' },
    shipToId: 'PNL',
    itemCode: 'MCCB-400A-3P',
    demandRef: null,
    needInDays: 50,
    demand: 30,
    onHand: 12,
    onOrder: 0,
    safetyStock: 6,
    weeklyUsage: 4,
    priority: 'standard',
    unitCost: 1_420,
    costSource: { kind: 'list' },
  }),
  line({
    n: 3,
    cartId: 'CRT-VITX-PNL',
    type: 'stock',
    state: 'trouble',
    holdNote: QUALITY_HOLD_NOTE,
    holdRef: { kind: 'alert', id: 'RSK-301' },
    shipToId: 'PNL',
    itemCode: 'MCCB-250A-3P',
    demandRef: null,
    needInDays: 52,
    demand: 40,
    onHand: 18,
    onOrder: 0,
    safetyStock: 8,
    weeklyUsage: 5,
    priority: 'fill',
    unitCost: 980,
    costSource: { kind: 'list' },
  }),
];

/** Unsent draft POs already open for a cart. */
export const SEED_OPEN_DRAFTS: OpenDraftPo[] = [
  {
    poNumber: 'PO-4500236',
    cartId: 'CRT-CORV-DAL',
    total: 38_192.4,
    lineCount: 1,
    status: 'Draft',
    createdLabel: 'today 08:14',
  },
  {
    poNumber: 'PO-4500237',
    cartId: 'CRT-HOLL-DAL',
    total: 61_480,
    lineCount: 2,
    status: 'Pending Approval',
    createdLabel: 'today 08:31',
  },
  {
    poNumber: 'PO-4500233',
    cartId: 'CRT-POLR-PNL',
    total: 12_450,
    lineCount: 1,
    status: 'Draft',
    createdLabel: 'yesterday 16:02',
  },
];

/** The first PO number the board mints; earlier numbers belong to other flows. */
export const FIRST_RELEASED_PO_SEQ = 4_500_240;

export function poNumberFor(seq: number): string {
  return `PO-${seq}`;
}
